import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from 'sonner';
import { 
  Clock, 
  Building2, 
  Users,
  LogOut,
  Plus,
  Trash2,
  ArrowLeft,
  UserPlus,
  Power,
  PowerOff,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyManagement = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyAdmins, setCompanyAdmins] = useState({});
  const [loading, setLoading] = useState(true);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [deleteCompanyOpen, setDeleteCompanyOpen] = useState(false);
  const [deleteAdminOpen, setDeleteAdminOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingAdmin, setIsDeletingAdmin] = useState(false);
  
  const [newCompany, setNewCompany] = useState({ name: '', address: '' });
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
      
      // Fetch admins for each company
      const adminsData = {};
      for (const company of response.data) {
        try {
          const adminsResponse = await axios.get(`${API}/companies/${company.id}/admins`);
          adminsData[company.id] = adminsResponse.data;
        } catch (e) {
          adminsData[company.id] = [];
        }
      }
      setCompanyAdmins(adminsData);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleAddCompany = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/companies`, newCompany);
      setCompanies(prev => [...prev, response.data]);
      setCompanyAdmins(prev => ({ ...prev, [response.data.id]: [] }));
      setAddCompanyOpen(false);
      setNewCompany({ name: '', address: '' });
      toast.success('Company created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/companies/${selectedCompany.id}/admins`, {
        ...newAdmin,
        company_id: selectedCompany.id
      });
      setCompanyAdmins(prev => ({
        ...prev,
        [selectedCompany.id]: [...(prev[selectedCompany.id] || []), response.data]
      }));
      setAddAdminOpen(false);
      setNewAdmin({ name: '', email: '', password: '' });
      toast.success('Company admin created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}`);
      setCompanies(prev => prev.filter(c => c.id !== selectedCompany.id));
      setDeleteCompanyOpen(false);
      setSelectedCompany(null);
      toast.success('Company deleted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedCompany) return;
    setIsSubmitting(true);
    try {
      const response = await axios.put(`${API}/companies/${selectedCompany.id}/status`);
      setCompanies(prev => prev.map(c => 
        c.id === selectedCompany.id 
          ? { ...c, active: response.data.active, deactivated_at: response.data.active ? null : new Date().toISOString() }
          : c
      ));
      setStatusDialogOpen(false);
      setSelectedCompany(null);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin || !selectedCompany) return;
    setIsDeletingAdmin(true);
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/admins/${selectedAdmin.id}`);
      // Update local state
      setCompanyAdmins(prev => ({
        ...prev,
        [selectedCompany.id]: prev[selectedCompany.id].filter(a => a.id !== selectedAdmin.id)
      }));
      toast.success(`Admin "${selectedAdmin.name}" has been deleted`);
      setDeleteAdminOpen(false);
      setSelectedAdmin(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    } finally {
      setIsDeletingAdmin(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/super-admin')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-[hsl(243,65%,20%)] flex items-center justify-center text-white">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <span className="font-bold font-['Manrope'] text-[hsl(243,65%,20%)]">ClockIn Pro</span>
              <p className="text-xs text-slate-500">Company Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Company Management</h1>
            <p className="text-slate-500">Create companies and assign administrators</p>
          </div>
          <Dialog open={addCompanyOpen} onOpenChange={setAddCompanyOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                data-testid="add-company-btn"
              >
                <Plus className="w-4 h-4" />
                Add Company
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Create New Company</DialogTitle>
                <DialogDescription>
                  Add a new company to the platform. You can assign administrators after creation.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddCompany} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    placeholder="Acme Corporation"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                    required
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-address">Address (Optional)</Label>
                  <Input
                    id="company-address"
                    placeholder="123 Main St, City"
                    value={newCompany.address}
                    onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                    data-testid="company-address-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAddCompanyOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                    disabled={isSubmitting}
                    data-testid="submit-company-btn"
                  >
                    {isSubmitting ? <div className="spinner" /> : 'Create Company'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Companies List */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b">
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Companies ({companies.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner" />
              </div>
            ) : companies.length === 0 ? (
              <div className="empty-state py-12">
                <Building2 className="empty-state-icon" />
                <p className="text-slate-500">No companies yet</p>
                <p className="text-sm text-slate-400">Create your first company to get started</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {companies.map((company) => (
                  <AccordionItem key={company.id} value={company.id} className={company.active === false ? 'opacity-60' : ''}>
                    <AccordionTrigger className="px-6 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`w-10 h-10 rounded-lg ${company.active === false ? 'bg-slate-400' : 'bg-[hsl(243,65%,20%)]'} text-white flex items-center justify-center font-semibold`}>
                          {company.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{company.name}</p>
                            {company.active === false ? (
                              <Badge variant="secondary" className="text-[10px] bg-red-100 text-red-700">
                                <XCircle className="w-3 h-3 mr-1" />
                                Inactive
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {companyAdmins[company.id]?.length || 0} admin(s) • Created {format(new Date(company.created_at), 'dd/MM/yyyy')}
                            {company.deactivated_at && ` • Deactivated ${format(new Date(company.deactivated_at), 'dd/MM/yyyy')}`}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4">
                      <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                        {/* Company Admins */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              Company Administrators
                            </h4>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1"
                              onClick={() => {
                                setSelectedCompany(company);
                                setAddAdminOpen(true);
                              }}
                              data-testid={`add-admin-${company.id}`}
                            >
                              <UserPlus className="w-3 h-3" />
                              Add Admin
                            </Button>
                          </div>
                          {companyAdmins[company.id]?.length === 0 ? (
                            <p className="text-sm text-slate-500">No administrators assigned yet</p>
                          ) : (
                            <div className="space-y-2">
                              {companyAdmins[company.id]?.map((admin) => (
                                <div 
                                  key={admin.id}
                                  className="flex items-center justify-between bg-white rounded-lg p-3"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-[hsl(243,65%,20%)] text-white flex items-center justify-center text-sm font-medium">
                                      {admin.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm">{admin.name}</p>
                                      <p className="text-xs text-slate-500">{admin.email}</p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                                    onClick={() => {
                                      setSelectedCompany(company);
                                      setSelectedAdmin(admin);
                                      setDeleteAdminOpen(true);
                                    }}
                                    data-testid={`delete-admin-${admin.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-between pt-2 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className={company.active === false 
                              ? "text-green-600 hover:text-green-700 hover:bg-green-50 hover:border-green-300" 
                              : "text-amber-600 hover:text-amber-700 hover:bg-amber-50 hover:border-amber-300"
                            }
                            onClick={() => {
                              setSelectedCompany(company);
                              setStatusDialogOpen(true);
                            }}
                            data-testid={`toggle-status-${company.id}`}
                          >
                            {company.active === false ? (
                              <>
                                <Power className="w-4 h-4 mr-1" />
                                Activate Company
                              </>
                            ) : (
                              <>
                                <PowerOff className="w-4 h-4 mr-1" />
                                Deactivate Company
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--error-bg))]"
                            onClick={() => {
                              setSelectedCompany(company);
                              setDeleteCompanyOpen(true);
                            }}
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Company
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Admin Dialog */}
      <Dialog open={addAdminOpen} onOpenChange={setAddAdminOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Add Company Administrator</DialogTitle>
            <DialogDescription>
              Create an administrator account for {selectedCompany?.name}. They will be able to manage employees and offices.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Full Name</Label>
              <Input
                id="admin-name"
                placeholder="John Doe"
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                required
                data-testid="admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@company.com"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                required
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="Create a password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                required
                data-testid="admin-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddAdminOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                disabled={isSubmitting}
                data-testid="submit-admin-btn"
              >
                {isSubmitting ? <div className="spinner" /> : 'Create Admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Company Dialog */}
      <AlertDialog open={deleteCompanyOpen} onOpenChange={setDeleteCompanyOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedCompany?.name}</strong>? 
              This will also delete all administrators, employees, offices, and time records associated with this company.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
              onClick={handleDeleteCompany}
              disabled={isSubmitting}
              data-testid="confirm-delete-company"
            >
              {isSubmitting ? <div className="spinner" /> : 'Delete Company'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Toggle Company Status Dialog */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {selectedCompany?.active === false ? (
                <>
                  <Power className="w-5 h-5 text-green-600" />
                  Activate Company
                </>
              ) : (
                <>
                  <PowerOff className="w-5 h-5 text-amber-600" />
                  Deactivate Company
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {selectedCompany?.active === false ? (
                <>
                  <p>
                    Are you sure you want to activate <strong>{selectedCompany?.name}</strong>?
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    <p className="font-medium mb-1">What happens when you activate:</p>
                    <ul className="list-disc list-inside space-y-1 text-green-700">
                      <li>All users will be able to log in again</li>
                      <li>Employees can clock in/out</li>
                      <li>Admins can manage the company</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    Are you sure you want to deactivate <strong>{selectedCompany?.name}</strong>?
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <p className="font-medium mb-1">What happens when you deactivate:</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      <li>All users will be unable to log in</li>
                      <li>All time records are preserved</li>
                      <li>You can reactivate at any time</li>
                    </ul>
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={selectedCompany?.active === false 
                ? "bg-green-600 hover:bg-green-700" 
                : "bg-amber-600 hover:bg-amber-700"
              }
              onClick={handleToggleStatus}
              disabled={isSubmitting}
              data-testid="confirm-toggle-status"
            >
              {isSubmitting ? <div className="spinner" /> : (selectedCompany?.active === false ? 'Activate' : 'Deactivate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Admin Confirmation Dialog */}
      <AlertDialog open={deleteAdminOpen} onOpenChange={setDeleteAdminOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Administrator
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-3">
                Are you sure you want to delete <strong>{selectedAdmin?.name}</strong> 
                {' '}from <strong>{selectedCompany?.name}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <p className="font-medium mb-1">This action cannot be undone.</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>The admin will lose access to the company</li>
                  <li>They will need to be re-invited to regain access</li>
                  <li>Historical records will remain intact</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedAdmin(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteAdmin}
              disabled={isDeletingAdmin}
              data-testid="confirm-delete-admin"
            >
              {isDeletingAdmin ? <div className="spinner" /> : 'Delete Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyManagement;
